import {
	buildInsertionContent,
	buildScopedOptions,
	extractPlaceholderAtOffset,
	formatDebugNode,
	getCaretPositionFromPoint,
	groupOptionsByType,
	isStructuredType
} from './structuredEditorUtils';

function createContextDebugInfo(payload) {
	const source = payload || {};
	return {
		clickedNode: source.clickedNode || '',
		clickedText: source.clickedText || '',
		ancestorNode: source.ancestorNode || '',
		ancestorType: source.ancestorType || '',
		ancestorTypeValue: source.ancestorTypeValue || '',
		resolvedPath: source.resolvedPath || '',
		definitionName: source.definitionName || '',
		reviewedNodes: source.reviewedNodes || [],
		debugSteps: source.debugSteps || []
	};
}

export default {
	data() {
		return {
			mainStructuredFieldsFilter: '',
			mainEditorContextPath: '',
			mainEditorSelection: null,
			mainEditorSavedRange: null,
			mainEditorContextHandler: null,
			mainEditorContextListenersAttached: false,
			mainEditorContextRetryTimer: null,
			lastClickPosition: null,
			contextDebugInfo: createContextDebugInfo()
		};
	},
	computed: {
		showMainContextPanel() {
			return this.tipo === 'templates/mail/' && (this.structuredDefinitionTree || this.mainEditorContextPath);
		},
		mainContextLabel() {
			if (!this.mainEditorContextPath) {
				return 'RAIZ';
			}

			const definition = this.getValor(this.mainEditorContextPath);
			return definition && definition.name ? definition.name : this.mainEditorContextPath;
		},
		mainContextOptions() {
			if (this.mainEditorContextPath) {
				let scopedDefinition = this.getValor(this.mainEditorContextPath);
				if (!scopedDefinition) {
					scopedDefinition = this.findDefinitionByPath(this.mainEditorContextPath);
				}
				if (scopedDefinition) {
					return buildScopedOptions(scopedDefinition);
				}
				if (this.structuredDefinitionTree) {
					return buildScopedOptions(this.structuredDefinitionTree);
				}
			}

			if (this.structuredDefinitionTree) {
				return buildScopedOptions(this.structuredDefinitionTree);
			}

			return [];
		},
		filteredMainContextOptions() {
			const filter = (this.mainStructuredFieldsFilter || '').toLowerCase();
			if (!filter) {
				return this.mainContextOptions;
			}

			return this.mainContextOptions.filter(option => {
				const name = (option.name || '').toLowerCase();
				const content = (option.content || '').toLowerCase();
				return name.includes(filter) || content.includes(filter);
			});
		},
		groupedMainContextOptions() {
			return groupOptionsByType(this.filteredMainContextOptions);
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.setupMainEditorContextTracking();
		});
	},
	beforeDestroy() {
		this.teardownMainEditorContextTracking();
		if (this.mainEditorContextRetryTimer) {
			clearTimeout(this.mainEditorContextRetryTimer);
			this.mainEditorContextRetryTimer = null;
		}
	},
	methods: {
		formatDebugNode,
		getCaretPositionFromPoint(x, y) {
			return getCaretPositionFromPoint(document, x, y);
		},
		normalizeDefinitionLookupPath(path) {
			if (!path) {
				return path;
			}
			if (path === '$root' || path === '$actual') {
				return '';
			}
			if (path.startsWith('$root.')) {
				return path.substring('$root.'.length);
			}
			if (path.startsWith('$actual.')) {
				return path.substring('$actual.'.length);
			}
			return path;
		},
		setContextDebugInfo(payload) {
			this.contextDebugInfo = createContextDebugInfo(payload);
		},
		getMainEditorElement() {
			const editor = this.$refs.mediumeditor && this.$refs.mediumeditor.editor ? this.$refs.mediumeditor.editor : null;
			if (editor && editor.elements && editor.elements.length) {
				return editor.elements[0];
			}

			return this.$el ? this.$el.querySelector('.medium-editor-element') : null;
		},
		setupMainEditorContextTracking(retryCount = 0) {
			if (this.mainEditorContextListenersAttached) {
				return;
			}

			const editorElement = this.getMainEditorElement();
			if (!editorElement) {
				if (retryCount < 8) {
					this.mainEditorContextRetryTimer = setTimeout(() => {
						this.setupMainEditorContextTracking(retryCount + 1);
					}, 250);
				}
				return;
			}

			this.mainEditorContextHandler = () => {
				this.updateMainEditorContextFromSelection();
			};

			['mouseup', 'keyup', 'input', 'focus'].forEach(eventName => {
				editorElement.addEventListener(eventName, this.mainEditorContextHandler);
			});
			document.addEventListener('selectionchange', this.mainEditorContextHandler);
			this.mainEditorContextListenersAttached = true;
			this.updateMainEditorContextFromSelection();
		},
		teardownMainEditorContextTracking() {
			if (!this.mainEditorContextListenersAttached || !this.mainEditorContextHandler) {
				return;
			}

			const editorElement = this.getMainEditorElement();
			if (editorElement) {
				['mouseup', 'keyup', 'input', 'focus'].forEach(eventName => {
					editorElement.removeEventListener(eventName, this.mainEditorContextHandler);
				});
			}

			document.removeEventListener('selectionchange', this.mainEditorContextHandler);
			this.mainEditorContextListenersAttached = false;
			this.mainEditorContextHandler = null;
			if (this.mainEditorContextRetryTimer) {
				clearTimeout(this.mainEditorContextRetryTimer);
				this.mainEditorContextRetryTimer = null;
			}
		},
		handleMainEditorZoneClick(event) {
			if (this.tipo !== 'templates/mail/') {
				return;
			}

			window.requestAnimationFrame(() => {
				this.lastClickPosition = { x: event.clientX, y: event.clientY };
				const clickedNode = this.getClickedEditorNode(event);
				if (!clickedNode) {
					this.setContextDebugInfo({
						definitionName: 'SIN NODO CLICADO'
					});
					this.updateMainEditorContextFromSelection();
					return;
				}

				this.updateMainEditorContextFromTarget(clickedNode);
			});
		},
		getClickedEditorNode(event) {
			const editorElement = this.getMainEditorElement();
			if (!editorElement || !event) {
				return null;
			}

			const caret = this.getCaretPositionFromPoint(event.clientX, event.clientY);
			if (caret && caret.node && editorElement.contains(caret.node)) {
				return caret.node;
			}

			return event.target || null;
		},
		findStructuredContextAncestor(startNode) {
			const editorElement = this.getMainEditorElement();
			if (!editorElement || !startNode) {
				return null;
			}

			const clickedElement = startNode.nodeType === Node.ELEMENT_NODE ? startNode : startNode.parentElement;
			let currentNode = clickedElement;
			const reviewedNodes = [];
			while (currentNode && currentNode.nodeType === Node.ELEMENT_NODE && (currentNode === editorElement || editorElement.contains(currentNode))) {
				const dataType = currentNode.getAttribute('data-type');
				if (isStructuredType(dataType)) {
					const contextPath = this.resolveDefinitionPathForElement(currentNode, false);
					reviewedNodes.push(this.formatDebugNode(currentNode));
					if (contextPath) {
						const definition = this.getValor(contextPath) || this.findDefinitionByPath(contextPath);
						return {
							element: currentNode,
							path: contextPath,
							definition,
							reviewedNodes
						};
					}
				}

				currentNode = currentNode.parentElement;
			}

			return null;
		},
		updateMainEditorContextFromTarget(target) {
			const editorElement = this.getMainEditorElement();
			if (!editorElement || !target || !editorElement.contains(target)) {
				this.setContextDebugInfo({
					clickedNode: this.formatDebugNode(target),
					clickedText: target && target.textContent ? target.textContent.trim().substring(0, 120) : '',
					definitionName: 'FUERA DEL EDITOR'
				});
				this.mainEditorContextPath = '';
				return;
			}

			const editor = this.$refs.mediumeditor && this.$refs.mediumeditor.editor ? this.$refs.mediumeditor.editor : null;
			if (editor && typeof editor.exportSelection === 'function') {
				this.mainEditorSelection = editor.exportSelection();
			}
			const nativeSel = window.getSelection();
			if (nativeSel && nativeSel.rangeCount) {
				this.mainEditorSavedRange = nativeSel.getRangeAt(0).cloneRange();
			} else {
				this.mainEditorSavedRange = null;
			}

			const contextAncestor = this.findStructuredContextAncestor(target);
			if (!contextAncestor) {
				this.setContextDebugInfo({
					clickedNode: this.formatDebugNode(target),
					clickedText: target && target.textContent ? target.textContent.trim().substring(0, 120) : '',
					definitionName: 'SIN ANCETRO ESTRUCTURADO'
				});
				this.mainEditorContextPath = '';
				return;
			}

			this.setContextDebugInfo({
				clickedNode: this.formatDebugNode(target),
				clickedText: target && target.textContent ? target.textContent.trim().substring(0, 120) : '',
				ancestorNode: this.formatDebugNode(contextAncestor.element),
				ancestorType: contextAncestor.element.getAttribute('data-type') || '',
				ancestorTypeValue: contextAncestor.element.getAttribute('data-type-value') || '',
				resolvedPath: contextAncestor.path || '',
				definitionName: contextAncestor.definition && contextAncestor.definition.name ? contextAncestor.definition.name : '',
				reviewedNodes: contextAncestor.reviewedNodes || []
			});
			this.mainEditorContextPath = contextAncestor.path || '';
		},
		updateMainEditorContextFromSelection() {
			if (this.tipo !== 'templates/mail/') {
				return;
			}

			const editorElement = this.getMainEditorElement();
			const selection = window.getSelection();
			if (!editorElement || !selection || !selection.rangeCount) {
				return;
			}

			const anchorNode = selection.anchorNode;
			if (!anchorNode) {
				return;
			}

			const anchorElement = anchorNode.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode.parentElement;
			if (!anchorElement || !editorElement.contains(anchorElement)) {
				return;
			}

			this.updateMainEditorContextFromTarget(anchorElement);
		},
		insertMainEditorOption(option) {
			const isNested = !!this.mainEditorContextPath;
			const content = buildInsertionContent(option, isNested);
			if (!content) {
				return;
			}

			const editor = this.$refs.mediumeditor && this.$refs.mediumeditor.editor ? this.$refs.mediumeditor.editor : null;
			if (!editor) {
				return;
			}

			// Capturar la selección en variables locales ANTES de focus():
			// el evento focus dispara updateMainEditorContextFromSelection que sobreescribiría
			// mainEditorSavedRange y mainEditorSelection con la posición 0.
			const savedRange = this.mainEditorSavedRange;
			const savedSelection = this.mainEditorSelection;

			const editorElement = this.getMainEditorElement();
			if (editorElement) {
				editorElement.focus();
			}

			// Restaurar posición usando Range nativo (más fiable que importSelection
			// tras un focus programático que puede mover el cursor al inicio).
			if (savedRange) {
				const sel = window.getSelection();
				if (sel) {
					sel.removeAllRanges();
					sel.addRange(savedRange);
				}
			} else if (savedSelection && typeof editor.importSelection === 'function') {
				editor.importSelection(savedSelection, true);
			}

			if (isStructuredType(option.tipo)) {
				// Para bloques estructurados (list/object) se usa inserción DOM directa para que
				// el nodo quede dentro del contenedor actual (ej: dentro de un data-type="object").
				// editor.pasteHTML extrae los <div> fuera del contenedor padre en contenteditable.
				this.insertDynamicContentAtSelection(content.html, content.text);
			} else if (typeof editor.pasteHTML === 'function') {
				editor.pasteHTML(`${content.html}&nbsp;`);
			} else {
				this.insertDynamicContentAtSelection(content.html, content.text);
			}

			this.changed();
			this.updateMainEditorContextFromSelection();
		},
		resolveDefinitionPathForElement(element, includeFieldName = true) {
			if (!element) {
				return null;
			}

			const replaceSingleActual = (inputPath, replacement) => {
				if (!inputPath || !replacement) {
					return inputPath || '';
				}
				return inputPath.replace(/\$actual(?=\.|$)/, replacement);
			};
			const mergeStructuredPath = (currentPath, ancestorValue) => {
				if (!currentPath) {
					return ancestorValue || '';
				}
				if (!ancestorValue) {
					return currentPath;
				}
				if (/\$actual(?=\.|$)/.test(ancestorValue)) {
					return replaceSingleActual(ancestorValue, currentPath);
				}
				if (/\$actual(?=\.|$)/.test(currentPath)) {
					return replaceSingleActual(currentPath, ancestorValue);
				}
				if (ancestorValue.startsWith('$root')) {
					return `${ancestorValue}.${currentPath}`;
				}
				if (currentPath.startsWith('$root')) {
					return `${currentPath}.${ancestorValue}`;
				}
				return `${ancestorValue}.${currentPath}`;
			};

			let debugSteps = [];
			let clickedText = '';
			let current = element;
			let caretOffset = null;

			if (element.nodeType === Node.TEXT_NODE) {
				clickedText = element.textContent || '';
				current = element.parentElement;
				if (this.lastClickPosition) {
					const caret = this.getCaretPositionFromPoint(this.lastClickPosition.x, this.lastClickPosition.y);
					if (caret && caret.node === element) {
						caretOffset = caret.offset;
					}
				}
			} else {
				clickedText = element.textContent || '';
				if (this.lastClickPosition) {
					const caret = this.getCaretPositionFromPoint(this.lastClickPosition.x, this.lastClickPosition.y);
					if (caret && caret.node && element.contains(caret.node) && caret.node.nodeType === Node.TEXT_NODE) {
						clickedText = caret.node.textContent || '';
						caretOffset = caret.offset;
					}
				}
			}

			let path = '';
			let fieldName = '';
			let structuredAncestors = [];

			let fieldNameMatch = null;
			if (typeof caretOffset === 'number') {
				fieldNameMatch = extractPlaceholderAtOffset(clickedText, caretOffset);
			}
			if (!fieldNameMatch) {
				const fieldMatch = (clickedText || '').trim().match(/\[([^\]]+)\]/);
				fieldNameMatch = fieldMatch && fieldMatch[1] ? fieldMatch[1] : null;
			}
			if (fieldNameMatch) {
				fieldName = fieldNameMatch.split(':')[0].trim();
				if (fieldName === '$actual') {
					fieldName = '';
				} else if (fieldName.startsWith('$actual.')) {
					fieldName = fieldName.substring('$actual.'.length);
				}
			}

			while (current && current.nodeType === Node.ELEMENT_NODE) {
				const tagName = current.tagName || '';
				const type = current.getAttribute && current.getAttribute('data-type');
				const value = current.getAttribute && current.getAttribute('data-type-value');
				debugSteps.push(`Nodo: ${tagName}, data-type: ${type}, data-type-value: ${value}`);

				if (isStructuredType(type) && value) {
					structuredAncestors.push({ tagName, type, value });
					if (value.startsWith('$root')) {
						debugSteps.push(`Parada: ancestro con $root encontrado en ${tagName} => ${value}`);
						break;
					}
				}

				if (tagName === 'BODY') {
					debugSteps.push('Parada: alcanzado BODY');
					break;
				}

				current = current.parentElement;
			}

				if (structuredAncestors.length > 0) {
					// Procesar de afuera (raiz) hacia adentro para construir la ruta absoluta.
					// Los ancestros se recogen innermost-first; se invierte para outer-to-inner.
					// $actual.prop se reduce a su segmento relativo antes de componer el path.
					const orderedAncestors = structuredAncestors.slice().reverse();
					for (let index = 0; index < orderedAncestors.length; index += 1) {
						let ancestorValue = orderedAncestors[index].value;
						if (ancestorValue.startsWith('$actual.')) {
							ancestorValue = ancestorValue.substring('$actual.'.length);
						}
						const previousPath = path;
						path = mergeStructuredPath(path, ancestorValue);
						debugSteps.push(`Composicion ancestro: ${previousPath} + ${ancestorValue} => ${path}`);
					}
				}

			if (includeFieldName && fieldName) {
				if (!path.endsWith(`.${fieldName}`) && path !== fieldName) {
					path = path ? `${path}.${fieldName}` : fieldName;
					debugSteps.push(`Campo añadido al final: ${fieldName}`);
				} else {
					debugSteps.push(`Campo ya incluido en el path: ${fieldName}`);
				}
			}

			debugSteps.push(`Resultado final: ${path}`);
			this.contextDebugInfo = createContextDebugInfo({
				clickedNode: this.contextDebugInfo.clickedNode,
				clickedText: this.contextDebugInfo.clickedText,
				ancestorNode: this.contextDebugInfo.ancestorNode,
				ancestorType: this.contextDebugInfo.ancestorType,
				ancestorTypeValue: this.contextDebugInfo.ancestorTypeValue,
				resolvedPath: path,
				definitionName: this.contextDebugInfo.definitionName,
				reviewedNodes: this.contextDebugInfo.reviewedNodes,
				debugSteps
			});
			return path;
		}
	}
};
